import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 标题和副标题样式已删除 - 当前未使用

// 横幅区域样式
const Banner = styled('div', {
  width: '100%',
  height: '400px',
  background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '2rem',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/assets/asic-miners-bg.jpg") center/cover no-repeat',
    opacity: 0.2,
    zIndex: 0
  }
});

// 横幅内容样式
const BannerContent = styled('div', {
  zIndex: 1,
  textAlign: 'center',
  color: 'white'
});

// 横幅标题样式
const BannerTitle = styled('h2', {
  fontSize: '3rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 横幅描述样式
const BannerDescription = styled('p', {
  fontSize: '1.25rem',
  maxWidth: '800px',
  marginBottom: '2rem'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.75rem 2rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9,
          transform: 'translateY(-2px)'
        }
      },
      secondary: {
        background: 'transparent',
        color: 'white',
        border: '2px solid white',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.1)'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 特点部分容器样式
const FeaturesContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2rem',
  marginTop: '2rem'
});

// 特点卡片样式
const FeatureCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// 特点图标样式
const FeatureIcon = styled('div', {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: '$accentPrimary',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '1rem'
});

// 特点标题样式
const FeatureTitle = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 特点描述样式
const FeatureDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary'
});

// 区块样式
const Section = styled('section', {
  marginBottom: '4rem'
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  color: '$textPrimary'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: '1.1rem',
  marginBottom: '2rem',
  color: '$textSecondary',
  maxWidth: '800px'
});

// 呼叫行动区块样式
const CtaSection = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '3rem',
  textAlign: 'center',
  marginTop: '3rem'
});

// 购买流程容器
const ProcessSteps = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1.5rem',
  margin: '2rem 0'
});

// 步骤项样式
const StepItem = styled('div', {
  flex: '1 1 200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  
  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    top: '2rem',
    right: '-2rem',
    width: '4rem',
    height: '2px',
    background: '$borderColor',
    
    '@media (max-width: 768px)': {
      display: 'none'
    }
  }
});

// 步骤编号样式
const StepNumber = styled('div', {
  width: '4rem',
  height: '4rem',
  borderRadius: '50%',
  background: '$accentPrimary',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: 'white',
  marginBottom: '1rem'
});

// 步骤文本样式
const StepText = styled('p', {
  fontSize: '1rem',
  textAlign: 'center',
  color: '$textSecondary'
});

/**
 * ASIC矿机商店概览页面组件
 * 提供商店介绍与产品概览
 */
const AsicShopOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 特点数据数组
  const features = [
    {
      icon: '🔌',
      title: t('asicShop.features.latestModels.title', '最新型号'),
      description: t('asicShop.features.latestModels.description', '提供市场上最新、最高效的ASIC矿机型号，确保最大的挖矿效率')
    },
    {
      icon: '🛠️',
      title: t('asicShop.features.customization.title', '定制服务'),
      description: t('asicShop.features.customization.description', '根据您的需求定制矿机配置，优化您的挖矿投资回报')
    },
    {
      icon: '🔒',
      title: t('asicShop.features.warranty.title', '全面保修'),
      description: t('asicShop.features.warranty.description', '所有矿机提供12个月全面保修，确保您的设备稳定运行')
    },
    {
      icon: '📦',
      title: t('asicShop.features.delivery.title', '全球配送'),
      description: t('asicShop.features.delivery.description', '提供全球配送服务，专业包装和物流追踪，确保您的设备安全送达')
    }
  ];
  
  // 购买流程步骤
  const purchaseSteps = [
    {
      number: 1,
      text: t('asicShop.purchaseSteps.select', '选择您需要的矿机型号')
    },
    {
      number: 2,
      text: t('asicShop.purchaseSteps.customize', '根据需求进行定制配置')
    },
    {
      number: 3,
      text: t('asicShop.purchaseSteps.order', '提交订单并完成支付')
    },
    {
      number: 4,
      text: t('asicShop.purchaseSteps.delivery', '等待矿机配送并开始挖矿')
    }
  ];
  
  return (
    <PageContainer>
      <Banner>
        <BannerContent>
          <BannerTitle>{t('asicShop.banner.title', 'ASIC矿机商店')}</BannerTitle>
          <BannerDescription>
            {t('asicShop.banner.description', '获取最高效、最先进的加密货币挖矿硬件，提升您的挖矿效率和收益')}
          </BannerDescription>
          <Link to="/products/asic-shop/products">
            <Button variant="primary">
              {t('asicShop.banner.browseProducts', '浏览矿机产品')}
            </Button>
          </Link>
        </BannerContent>
      </Banner>
      
      <Section>
        <SectionTitle>{t('asicShop.intro.title', '为什么选择我们的矿机')}</SectionTitle>
        <SectionDescription>
          {t('asicShop.intro.description', 'BestMine提供市场上最高效、最可靠的ASIC矿机，从入门级到专业级设备应有尽有。我们与顶级制造商直接合作，确保您能获得最具竞争力的价格和最优质的产品。')}
        </SectionDescription>
        
        <FeaturesContainer>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesContainer>
      </Section>
      
      <Section>
        <SectionTitle>{t('asicShop.purchase.title', '购买流程')}</SectionTitle>
        <SectionDescription>
          {t('asicShop.purchase.description', '我们简化了矿机购买流程，让您能够快速获取所需设备并开始挖矿之旅。')}
        </SectionDescription>
        
        <ProcessSteps>
          {purchaseSteps.map((step, index) => (
            <StepItem key={index}>
              <StepNumber>{step.number}</StepNumber>
              <StepText>{step.text}</StepText>
            </StepItem>
          ))}
        </ProcessSteps>
      </Section>
      
      <CtaSection>
        <SectionTitle>{t('asicShop.cta.title', '准备好开始您的挖矿之旅了吗？')}</SectionTitle>
        <SectionDescription style={{ margin: '0 auto 2rem' }}>
          {t('asicShop.cta.description', '浏览我们的产品目录，找到最适合您需求的ASIC矿机，开始您的加密货币挖矿之旅。')}
        </SectionDescription>
        <Link to="/products/asic-shop/products">
          <Button variant="primary">
            {t('asicShop.cta.browseProducts', '浏览矿机产品')}
          </Button>
        </Link>
      </CtaSection>
    </PageContainer>
  );
};

export default AsicShopOverview; 